import type { Key } from "react-aria-components";

import { useMemo, useState } from "react";
import { ListLayout, Virtualizer } from "react-aria-components";

import { Avatar } from "@/components/avatar";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import { FieldError, Label } from "@/components/field";
import { Tag, TagGroup, TagList } from "@/components/tag-group";
import { Small } from "@/components/text";

export interface MultiSelect2Option {
  avatar?: string;
  description?: null | string;
  href?: string;
  id: number;
  isInactive?: boolean;
  textValue: string;
}

export interface MultiSelect2Props {
  className?: string;
  description?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  label?: string;
  onChange?: (ids: number[]) => void;
  onRemove?: (key: Key | null) => void;
  onSelect?: (key: Key | null) => void;
  options: MultiSelect2Option[];
  placeholder?: string;
  requiredHint?: boolean;
  selectedIds: number[];
}

export function MultiSelect2({
  className,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  label,
  onChange,
  onRemove,
  onSelect,
  options,
  placeholder,
  requiredHint,
  selectedIds,
}: MultiSelect2Props) {
  const [inputValue, setInputValue] = useState("");

  const uniqueSelectedIds = useMemo(
    () => [...new Set(selectedIds)],
    [selectedIds],
  );

  const handleSelectionChange = (key: Key | null) => {
    if (key) {
      onSelect?.(key);
      onChange?.([...uniqueSelectedIds, Number(key)]);
      setInputValue("");
    }
  };

  const unselectedOptions = useMemo(() => {
    const lowerInput = inputValue.toLowerCase();
    const selectedIdSet = new Set(uniqueSelectedIds);

    return options.filter((item) => {
      if (selectedIdSet.has(item.id)) return false;

      if (!lowerInput) return true;

      return item.textValue.toLowerCase().includes(lowerInput);
    });
  }, [options, uniqueSelectedIds, inputValue]);

  const selectedOptions = useMemo(() => {
    const optionsById = new Map(options.map((item) => [item.id, item]));

    return uniqueSelectedIds.map(
      (id) => optionsById.get(id) ?? { id, textValue: `#${id}` },
    );
  }, [options, uniqueSelectedIds]);

  const layout = useMemo(() => new ListLayout({ estimatedRowHeight: 60 }), []);

  return (
    <div className="flex flex-col gap-2">
      <ComboBox
        allowsEmptyCollection
        className={className}
        inputValue={inputValue}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        items={unselectedOptions}
        menuTrigger="focus"
        onInputChange={setInputValue}
        onSelectionChange={handleSelectionChange}
        selectedKey={null}
      >
        {label && (
          <Label
            className="text-sm font-medium text-text-secondary"
            requiredHint={requiredHint}
          >
            {label}
          </Label>
        )}
        <ComboBoxGroup>
          <ComboBoxInput placeholder={placeholder} />
          <ComboBoxButton />
        </ComboBoxGroup>
        {description && (
          <Small className="text-text-tertiary">{description}</Small>
        )}
        <FieldError>{errorMessage}</FieldError>
        <ComboBoxPopover>
          <Virtualizer layout={layout}>
            <ComboBoxListBox<MultiSelect2Option>
              items={unselectedOptions}
              renderEmptyState={() => (
                <div className="py-3 px-4 text-md text-text-tertiary">
                  No options available
                </div>
              )}
            >
              {(item) => (
                <ComboBoxListItem id={item.id} textValue={item.textValue}>
                  <div
                    className={`flex flex-col ${item.isInactive ? "opacity-50" : ""}`}
                  >
                    <span className="text-sm font-medium">
                      {item.textValue} {item.isInactive && "(Inactive)"}
                    </span>
                    {item.description && (
                      <span className="text-xs text-text-tertiary">
                        {item.description}
                      </span>
                    )}
                  </div>
                </ComboBoxListItem>
              )}
            </ComboBoxListBox>
          </Virtualizer>
        </ComboBoxPopover>
      </ComboBox>

      {selectedOptions.length > 0 && (
        <>
          <div className="text-xs text-text-tertiary">
            {selectedOptions.length} items selected
          </div>
          <TagGroup
            className="border-dashed border-border-primary border p-md rounded-md"
            onRemove={(keys) => {
              const keyArray = [...keys];

              for (const key of keyArray) {
                onRemove?.(key);
              }
              onChange?.(uniqueSelectedIds.filter((id) => !keys.has(id)));
            }}
          >
            <TagList items={selectedOptions}>
              {(item) => (
                <Tag
                  className={`rounded-full py-1 pr-3 pl-1.5 border-utility-gray-700 border-[1.5px] ${item.isInactive ? "opacity-50" : ""} ${
                    item.href ? "cursor-pointer hover:bg-bg-secondary" : ""
                  }`}
                  href={item.href}
                  id={item.id}
                  textValue={item.textValue}
                >
                  <Avatar
                    alt={item.textValue}
                    className="size-4"
                    src={item.avatar || undefined}
                  />
                  <span className="text-sm font-bold">{item.textValue}</span>
                </Tag>
              )}
            </TagList>
          </TagGroup>
        </>
      )}
    </div>
  );
}
